/**
 * Members tab — searchable list of customers with points, retention progress, and DOB.
 *
 * Features 2 & 8: retention columns (orders/spending 12m) + DOB + birthday filter.
 * Search is debounced 300ms. Pagination via "Load more" button.
 *
 * Plan 177, Epic 07 + Plan 1833, Features 2 & 8
 */

import { useState, useEffect, useRef } from "react";
import { Search, Users, Cake, ShoppingCart, Banknote } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useMembersList, type MemberItem } from "../api/rewardsAdminHooks";
import { MemberDetail } from "./MemberDetail";

const PAGE_SIZE = 20;

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("th-TH", { day: "numeric", month: "short" });
}

function isBirthdayThisMonth(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  return d.getMonth() === now.getMonth();
}

export function MembersTab() {
  const [selectedMember, setSelectedMember] = useState<MemberItem | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [birthdayFilter, setBirthdayFilter] = useState<string | undefined>();
  // previousPages stores members from pages BEFORE the current offset
  const [previousPages, setPreviousPages] = useState<MemberItem[]>([]);
  const [offset, setOffset] = useState(0);

  // Debounce search input — only fires when value actually changes
  const prevSearchRef = useRef(search);
  useEffect(() => {
    if (search === prevSearchRef.current) return;
    prevSearchRef.current = search;
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setOffset(0);
      setPreviousPages([]);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset on birthday filter change — use ref to skip mount
  const prevFilterRef = useRef(birthdayFilter);
  useEffect(() => {
    if (birthdayFilter === prevFilterRef.current) return;
    prevFilterRef.current = birthdayFilter;
    setOffset(0);
    setPreviousPages([]);
  }, [birthdayFilter]);

  const membersQuery = useMembersList(
    debouncedSearch,
    PAGE_SIZE,
    offset,
    true,
    birthdayFilter,
  );

  // Derive display list directly from query data — no effect delay
  const currentPageMembers = membersQuery.data?.members ?? [];
  const allMembers = offset === 0 ? currentPageMembers : [...previousPages, ...currentPageMembers];
  const hasMore = membersQuery.data?.has_more ?? false;

  const handleLoadMore = () => {
    // Snapshot all currently displayed members before fetching next page
    setPreviousPages(allMembers);
    setOffset((prev) => prev + PAGE_SIZE);
  };

  const toggleBirthdayFilter = () => {
    setBirthdayFilter((prev) =>
      prev === "this_month" ? undefined : "this_month",
    );
  };

  if (selectedMember) {
    return (
      <MemberDetail
        member={selectedMember}
        onClose={() => setSelectedMember(null)}
      />
    );
  }

  if (membersQuery.isLoading && allMembers.length === 0) {
    return (
      <div className="space-y-3 pt-4">
        <Skeleton className="h-10 rounded-lg" />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-16 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-4">
      {/* Search + birthday filter */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="ค้นหาสมาชิก..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          variant={birthdayFilter === "this_month" ? "default" : "outline"}
          size="sm"
          onClick={toggleBirthdayFilter}
          className="shrink-0 gap-1.5"
        >
          <Cake className="h-3.5 w-3.5" />
          วันเกิดเดือนนี้
        </Button>
      </div>

      {/* Members list */}
      {allMembers.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <Users className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground">
            {debouncedSearch || birthdayFilter
              ? "ไม่พบสมาชิกที่ค้นหา"
              : "ยังไม่มีสมาชิก"}
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          {allMembers.map((member) => (
            <div
              key={member.customer}
              className="flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => setSelectedMember(member)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">
                    {member.customer_name}
                  </p>
                  {isBirthdayThisMonth(member.date_of_birth) && (
                    <Cake className="h-3.5 w-3.5 text-pink-500 shrink-0" />
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="truncate">{member.customer}</span>
                  {member.date_of_birth && (
                    <span className="shrink-0">
                      DOB: {formatDate(member.date_of_birth)}
                    </span>
                  )}
                </div>
              </div>

              {/* Retention progress */}
              <div className="hidden sm:flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                <span className="flex items-center gap-1" title="ออเดอร์ 12 เดือน">
                  <ShoppingCart className="h-3 w-3" />
                  {member.orders_last_12m}
                </span>
                <span className="flex items-center gap-1" title="ยอดใช้จ่าย 12 เดือน">
                  <Banknote className="h-3 w-3" />
                  {member.spent_last_12m > 0
                    ? `฿${member.spent_last_12m.toLocaleString()}`
                    : "฿0"}
                </span>
              </div>

              {member.tier && (
                <Badge variant="secondary" className="text-xs shrink-0">
                  {member.tier}
                </Badge>
              )}
              <p className="text-sm font-semibold text-amber-600 whitespace-nowrap">
                {member.points_balance.toLocaleString()} แต้ม
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Load more */}
      {hasMore && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLoadMore}
          disabled={membersQuery.isFetching}
          className="w-full"
        >
          {membersQuery.isFetching ? "กำลังโหลด..." : "โหลดเพิ่มเติม"}
        </Button>
      )}
    </div>
  );
}
